import { css } from '@emotion/react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { createUuid } from '~/_shared/utils/createUuid';
import { useTranslation } from '~/_shared/utils/hooks';
import { useIsNumericColumnCheck } from '~/heatMap/panel/form/useIsNumericColumnCheck.hook';
import type { ModalProps } from '~/modal/modalType.enum';
import { ActiveBoundaryFillEditComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/activeBoundaryFillEditComponent';
import { useActiveBoundaryFillEdit } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/useActiveBoundaryFillEdit';
import { extraBoundaryFillAdded } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';
import { createBoundaryFillConfig } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.utils';
import { useVerifyNumericFillData } from '~/sidebar/sidebarApps/mapTools/boundary/fill/useVerifyNumericFillData';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';

export type BoundaryAddExtraFillProps = ModalProps<{
  boundaryTerritoryGroupId: number;
  boundaryGroupId: number;
}>;

const editWrapperStyle = css({
  padding: '5px 16px',
});

const excludedBoundaryTerritoryTypes = [BoundaryTerritoryType.Manual];

export const AddExtraFillComponent = ({
  onClose,
  isOpen,
  boundaryTerritoryGroupId,
  boundaryGroupId,
}: BoundaryAddExtraFillProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { checkIsNumeric, isLoading: isCheckNumericLoading } = useIsNumericColumnCheck();
  const { verifyNumericFillData } = useVerifyNumericFillData({ checkIsNumeric });
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const boundaryTerritoryGroup = boundaryTerritoryGroups.find(
    group => group.boundaryTerritoryGroupId === boundaryTerritoryGroupId,
  );

  const {
    isUpdateDisabled, boundaryFillModel, ...boundaryFillProps
  } = useActiveBoundaryFillEdit({
    boundaryGroupId,
    hiddenBoundaryTerritoryTypeOptions: excludedBoundaryTerritoryTypes,
  });

  const {
    boundaryFillData,
    boundaryTerritoryType,
    numericCalculateBucketFunction,
    selectedDemographicId,
    numberOfRanges,
  } = boundaryFillModel;

  const handleConfirm = useCallback(() => {
    const newFillConfig = createBoundaryFillConfig({
      boundaryFillData,
      boundaryFillId: createUuid(),
      boundaryTerritoryType,
      numericCalculateBucketFunction,
      selectedDemographicId,
      rangesCount: numberOfRanges,
      style: boundaryTerritoryGroup?.settings.style,
    });
    if (!isUpdateDisabled && newFillConfig) {
      dispatch(extraBoundaryFillAdded({
        boundaryTerritoryGroupId,
        boundaryFillConfig: newFillConfig,
      }));
      onClose();
    }
  },
  [boundaryFillData, boundaryTerritoryGroup?.settings.style, boundaryTerritoryGroupId, boundaryTerritoryType,
    dispatch, isUpdateDisabled, numberOfRanges, numericCalculateBucketFunction, onClose, selectedDemographicId]);

  const updateBoundaryTerritoryGroup = useCallback(async () => {
    const {
      boundaryTerritoryType,
      boundaryFillData,
    } = boundaryFillModel;
    if (!boundaryTerritoryGroup || !boundaryTerritoryType) {
      return;
    }

    if (boundaryTerritoryType === BoundaryTerritoryType.Numeric) {
      if (!await verifyNumericFillData(boundaryFillData)) {
        return;
      }
    }

    handleConfirm();
  }, [boundaryFillModel, boundaryTerritoryGroup, handleConfirm, verifyNumericFillData]);

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      caption="Add extra fill"
      leftFooterContent={isCheckNumericLoading ? <OverlayLoaderComponent /> : null}
      confirmButton={(
        <TooltipComponent
          tooltipContent={boundaryFillProps.updateDisabledReason}
          placement={TooltipPlacement.Bottom}
        >
          <ButtonComponent
            prefixIcon={faCheck}
            text={t('Save')}
            onClick={updateBoundaryTerritoryGroup}
            isDisabled={isUpdateDisabled || isCheckNumericLoading}
          />
        </TooltipComponent>
      )}
    >
      <div css={editWrapperStyle}>
        <ActiveBoundaryFillEditComponent
          labelCountStart={1}
          {...boundaryFillProps}
          {...boundaryFillModel}
        />
      </div>
    </ModalComponent>
  );
};
